import React from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Text, Flex, Button
} from 'components/ui';
import Icon from 'components/Icon';
import ProductImage from 'components/Product/ProductImage';
import AddToBasketButton from 'components/AddToBasketButton';
import {
    colors, space, mediaQueries, fontSizes, fontWeights, lineHeights, shadows
} from 'style/config';
import { MAX_PRODUCTS } from 'components/Catalog/ProductGrid/CompareModal/constants';
import { zoneStyles } from 'components/Catalog/ProductGrid/CompareModal/Zones/zoneStyles';
import { CARD_IMG_SIZE_GRID_XX_LARGE } from 'constants/productCard';
import helpersUtils from 'utils/Helpers';

const { formatDiscountPrice, appendDollarSign } = helpersUtils;
const ProductCardZone = ({
    comparisonData = [], getText = () => {}, handleProductRemove = () => {}, scrollRef, onScroll, isCompact = false
}) => {
    // Render product card content for a specific product
    const renderProductCardContent = (product, index) => {
        const isEmpty = !product;
        const currentProduct = product?.parentProducts?.[0];
        const slotKey = currentProduct?.productId ? `product-${currentProduct?.productId}` : `slot-${index}`;
        const imageProps = product
            ? {
                size: CARD_IMG_SIZE_GRID_XX_LARGE,
                src: product?.primaryImages[0]?.baseUrl
            }
            : {};

        return (
            <Box
                key={slotKey}
                css={zoneStyles.productSlot}
                data-at={Sephora.debug.dataAt(`compare_modal_product_slot_${index + 1}`)}
            >
                {isEmpty ? null : (
                    <Box css={isCompact ? styles.productCardZoneCompact : styles.productCardZone}>
                        {!isCompact && (
                            <Button
                                css={styles.closeButton}
                                onClick={() => handleProductRemove(currentProduct.productId)}
                                data-at={Sephora.debug.dataAt(`compare_modal_remove_product_${index + 1}`)}
                                aria-label={getText('removeProduct')}
                            >
                                <Icon
                                    name='x'
                                    size={14}
                                />
                            </Button>
                        )}
                        <Box css={isCompact ? styles.imageContainerCompact : styles.imageContainer}>
                            <ProductImage {...imageProps} />
                        </Box>
                        <Box css={isCompact ? styles.productInfoCompact : styles.productInfo}>
                            <Box css={styles.productDetails}>
                                {currentProduct?.brand?.displayName && <Text css={styles.brandName}>{currentProduct?.brand?.displayName}</Text>}
                                {currentProduct?.name && (
                                    <Text
                                        css={isCompact ? styles.displayNameCompact : styles.displayName}
                                        numberOfLines={isCompact ? 1 : 2}
                                    >
                                        {currentProduct?.name}
                                    </Text>
                                )}
                                {product?.skuPrices[0] && (
                                    <Text css={styles.price}>{appendDollarSign(formatDiscountPrice(product?.skuPrices[0].price))}</Text>
                                )}
                            </Box>
                            {product?.currentSku?.skuId && (
                                <Box css={styles.addToBasketContainer}>
                                    <AddToBasketButton
                                        sku={product.currentSku}
                                        product={currentProduct}
                                        productId={currentProduct.productId}
                                        dataAt={`compare_modal_add_to_basket_${index + 1}`}
                                        variant='special'
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <Box
            ref={scrollRef}
            css={[styles.stickyProductCardSection, isCompact && styles.stickyProductCardSectionCompact]}
            onScroll={onScroll}
        >
            <Flex css={zoneStyles.productsContainer}>
                {Array.from({ length: MAX_PRODUCTS }, (_, index) => {
                    const product = comparisonData?.skus?.[index];

                    return renderProductCardContent(product, index);
                })}
            </Flex>
        </Box>
    );
};

const styles = {
    stickyProductCardSection: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: colors.white,
        paddingTop: `${space[5]}px`,
        paddingBottom: `${space[2]}px`,
        paddingX: `${space[4]}px`,
        marginBottom: `${space[4]}px`,
        overflowX: 'scroll',
        overflowY: 'hidden',
        // Hide scrollbars but allow scrolling
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
            display: 'none'
        },
        transition: 'padding 0.3s ease',
        [mediaQueries.sm]: {
            paddingX: `${space[4]}px`
        }
    },
    stickyProductCardSectionCompact: {
        paddingTop: `${space[2]}px`,
        paddingBottom: `${space[2]}px`,
        boxShadow: shadows.light,
        marginLeft: `-${space[4]}px`,
        paddingLeft: `${space[4]}px`,
        marginRight: `-${space[4]}px`,
        marginBottom: 0
    },
    productCardZone: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
    },
    productCardZoneCompact: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'row',
        gap: `${space[1]}px`,
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center'
    },
    closeButton: {
        position: 'absolute',
        top: 0,
        right: 0,
        width: '14px',
        height: '14px',
        minWidth: '14px',
        minHeight: '14px',
        padding: 0,
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1,
        '&:hover': {
            opacity: 0.7
        }
    },
    imageContainer: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
        transition: 'all 0.3s ease'
    },
    imageContainerCompact: {
        width: '60px',
        height: '60px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
        '& img': {
            width: '100%',
            height: '100%',
            objectFit: 'cover'
        }
    },
    productInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: `${space[1]}px`,
        paddingX: `${space[2]}px`,
        paddingBottom: `${space[2]}px`,
        flex: 1,
        justifyContent: 'space-between'
    },
    productDetails: {
        display: 'flex',
        flexDirection: 'column',
        gap: `${space[1]}px`
    },
    brandName: {
        fontSize: `${fontSizes.xs}px`,
        fontWeight: fontWeights.bold,
        lineHeight: lineHeights.tight,
        color: colors.black,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    displayName: {
        fontSize: `${fontSizes.sm}px`,
        fontWeight: fontWeights.normal,
        lineHeight: lineHeights.tight,
        color: colors.black,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    displayNameCompact: {
        whiteSpace: 'no-wrap',
        fontSize: `${fontSizes.sm}px`,
        fontWeight: fontWeights.normal,
        lineHeight: lineHeights.tight,
        color: colors.black,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    price: {
        fontSize: `${fontSizes.base}px`,
        fontWeight: fontWeights.bold,
        lineHeight: lineHeights.tight,
        color: colors.black
    },
    addToBasketContainer: {
        width: '100%',
        marginTop: `${space[1]}px`
    }
};

ProductCardZone.propTypes = {
    selectedProducts: PropTypes.arrayOf(PropTypes.object),
    comparisonData: PropTypes.object,
    getText: PropTypes.func,
    handleProductRemove: PropTypes.func,
    scrollRef: PropTypes.object,
    onScroll: PropTypes.func,
    isCompact: PropTypes.bool
};

export default wrapFunctionalComponent(ProductCardZone, 'ProductCardZone');
